/**
 * SPH Particle Point Sprite / Billboard Renderer
 *
 * Renders particles as camera-facing quads with sphere impostor shading.
 * Supports multiple color modes: solid, velocity, pressure, density, age, source, temperature, heatmap.
 */

export const PARTICLE_VERTEX_SHADER = /* wgsl */`
struct VertexOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) color: vec4<f32>,
  @location(1) pointCoord: vec2<f32>,
  @location(2) viewPos: vec3<f32>,
  @location(3) particleRadius: f32,
}

struct RenderParams {
  viewMatrix: mat4x4<f32>,
  projMatrix: mat4x4<f32>,
  invProjMatrix: mat4x4<f32>,
  cameraPos: vec4<f32>,
  screenSize: vec2<f32>,
  particleScale: f32,
  colorMode: u32, // 0=solid, 1=velocity, 2=pressure, 3=density, 4=age, 5=source, 6=temperature, 7=heatmap
  minVal: f32,
  maxVal: f32,
  _pad0: f32,
  _pad1: f32,
}

@group(0) @binding(0) var<uniform> renderParams: RenderParams;
@group(0) @binding(1) var<storage, read> positionMass: array<vec4<f32>>;
@group(0) @binding(2) var<storage, read> velocityAge: array<vec4<f32>>;
@group(0) @binding(3) var<storage, read> densityMisc: array<vec4<f32>>; // x=density, y=pressure, z=temperature, w=sourceId
@group(0) @binding(4) var<storage, read> heatmapData: array<f32>;

// Viridis-like colormap (perceptually uniform)
fn colormap(t: f32) -> vec3<f32> {
  let c0 = vec3<f32>(0.267004, 0.004874, 0.329415);
  let c1 = vec3<f32>(0.282623, 0.140926, 0.457517);
  let c2 = vec3<f32>(0.253935, 0.265254, 0.529983);
  let c3 = vec3<f32>(0.206756, 0.371758, 0.553117);
  let c4 = vec3<f32>(0.163625, 0.471133, 0.558148);
  let c5 = vec3<f32>(0.127568, 0.566949, 0.550556);
  let c6 = vec3<f32>(0.134692, 0.658636, 0.517649);
  let c7 = vec3<f32>(0.266941, 0.748751, 0.440573);
  let c8 = vec3<f32>(0.477504, 0.821444, 0.318195);
  let c9 = vec3<f32>(0.741388, 0.873449, 0.149561);
  let c10 = vec3<f32>(0.993248, 0.906157, 0.143936);

  let t_clamped = clamp(t, 0.0, 1.0);
  let segment = t_clamped * 10.0;
  let idx = floor(segment);
  let frac = segment - idx;

  if (idx < 1.0) { return mix(c0, c1, frac); }
  if (idx < 2.0) { return mix(c1, c2, frac); }
  if (idx < 3.0) { return mix(c2, c3, frac); }
  if (idx < 4.0) { return mix(c3, c4, frac); }
  if (idx < 5.0) { return mix(c4, c5, frac); }
  if (idx < 6.0) { return mix(c5, c6, frac); }
  if (idx < 7.0) { return mix(c6, c7, frac); }
  if (idx < 8.0) { return mix(c7, c8, frac); }
  if (idx < 9.0) { return mix(c8, c9, frac); }
  return mix(c9, c10, frac);
}

// Thermal colormap (blue -> cyan -> green -> yellow -> red)
fn colormapThermal(t: f32) -> vec3<f32> {
  let t_clamped = clamp(t, 0.0, 1.0);
  if (t_clamped < 0.25) {
    let u = t_clamped * 4.0;
    return mix(vec3<f32>(0.0, 0.0, 1.0), vec3<f32>(0.0, 1.0, 1.0), u);
  } else if (t_clamped < 0.5) {
    let u = (t_clamped - 0.25) * 4.0;
    return mix(vec3<f32>(0.0, 1.0, 1.0), vec3<f32>(0.0, 1.0, 0.0), u);
  } else if (t_clamped < 0.75) {
    let u = (t_clamped - 0.5) * 4.0;
    return mix(vec3<f32>(0.0, 1.0, 0.0), vec3<f32>(1.0, 1.0, 0.0), u);
  } else {
    let u = (t_clamped - 0.75) * 4.0;
    return mix(vec3<f32>(1.0, 1.0, 0.0), vec3<f32>(1.0, 0.0, 0.0), u);
  }
}

// HSV to RGB conversion for source coloring
fn hsv2rgb(hsv: vec3<f32>) -> vec3<f32> {
  let h = hsv.x * 6.0;
  let s = hsv.y;
  let v = hsv.z;
  let c = v * s;
  let x = c * (1.0 - abs((h % 2.0) - 1.0));
  let m = v - c;

  var rgb = vec3<f32>(0.0);
  if (h < 1.0) { rgb = vec3<f32>(c, x, 0.0); }
  else if (h < 2.0) { rgb = vec3<f32>(x, c, 0.0); }
  else if (h < 3.0) { rgb = vec3<f32>(0.0, c, x); }
  else if (h < 4.0) { rgb = vec3<f32>(0.0, x, c); }
  else if (h < 5.0) { rgb = vec3<f32>(x, 0.0, c); }
  else { rgb = vec3<f32>(c, 0.0, x); }

  return rgb + m;
}

@vertex
fn main(
  @builtin(vertex_index) vertexIndex: u32,
  @builtin(instance_index) instanceIndex: u32
) -> VertexOutput {
  var output: VertexOutput;

  // Quad corners for billboard (-1,-1), (1,-1), (-1,1), (1,1)
  var corners = array<vec2<f32>, 4>(
    vec2<f32>(-1.0, -1.0),
    vec2<f32>(1.0, -1.0),
    vec2<f32>(-1.0, 1.0),
    vec2<f32>(1.0, 1.0)
  );
  let corner = corners[vertexIndex];
  output.pointCoord = corner * 0.5 + 0.5; // [0,1] range

  // Get particle data
  let pos = positionMass[instanceIndex];
  let vel = velocityAge[instanceIndex];
  let misc = densityMisc[instanceIndex];

  let particlePos = pos.xyz;
  let particleMass = pos.w;
  let velocity = vel.xyz;
  let age = vel.w;
  let density = misc.x;
  let pressure = misc.y;
  let temperature = misc.z;
  let sourceId = u32(misc.w);

  // Transform to view space
  let viewPos = (renderParams.viewMatrix * vec4<f32>(particlePos, 1.0)).xyz;
  output.viewPos = viewPos;

  // Compute particle radius (scale by mass if needed)
  let baseRadius = 0.05; // Base particle radius
  output.particleRadius = baseRadius * renderParams.particleScale * sqrt(particleMass);

  // Billboard: extract camera right and up vectors from inverse view matrix
  let right = normalize(vec3<f32>(renderParams.viewMatrix[0][0], renderParams.viewMatrix[1][0], renderParams.viewMatrix[2][0]));
  let up = normalize(vec3<f32>(renderParams.viewMatrix[0][1], renderParams.viewMatrix[1][1], renderParams.viewMatrix[2][1]));

  // Offset position by corner
  let worldPos = particlePos + (right * corner.x + up * corner.y) * output.particleRadius;
  let clipPos = renderParams.projMatrix * vec4<f32>((renderParams.viewMatrix * vec4<f32>(worldPos, 1.0)).xyz, 1.0);
  output.position = clipPos;

  // Compute color based on mode
  var color = vec3<f32>(0.3, 0.6, 0.9); // Default blue

  switch (renderParams.colorMode) {
    case 0u: { // Solid color
      color = vec3<f32>(0.3, 0.6, 0.9);
    }
    case 1u: { // Velocity magnitude
      let speed = length(velocity);
      let t = clamp((speed - renderParams.minVal) / (renderParams.maxVal - renderParams.minVal), 0.0, 1.0);
      color = colormapThermal(t);
    }
    case 2u: { // Pressure
      let t = clamp((pressure - renderParams.minVal) / (renderParams.maxVal - renderParams.minVal), 0.0, 1.0);
      color = colormap(t);
    }
    case 3u: { // Density
      let t = clamp((density - renderParams.minVal) / (renderParams.maxVal - renderParams.minVal), 0.0, 1.0);
      color = colormap(t);
    }
    case 4u: { // Age
      let t = clamp(age / max(renderParams.maxVal, 1.0), 0.0, 1.0);
      color = mix(vec3<f32>(0.0, 1.0, 0.0), vec3<f32>(1.0, 0.0, 0.0), t);
    }
    case 5u: { // Source ID
      let hue = f32(sourceId % 12u) / 12.0;
      color = hsv2rgb(vec3<f32>(hue, 0.8, 0.9));
    }
    case 6u: { // Temperature
      let t = clamp((temperature - renderParams.minVal) / (renderParams.maxVal - renderParams.minVal), 0.0, 1.0);
      color = colormapThermal(t);
    }
    case 7u: { // Heatmap (custom data)
      let heatValue = heatmapData[instanceIndex];
      let t = clamp((heatValue - renderParams.minVal) / (renderParams.maxVal - renderParams.minVal), 0.0, 1.0);
      color = colormap(t);
    }
    default: {
      color = vec3<f32>(0.3, 0.6, 0.9);
    }
  }

  output.color = vec4<f32>(color, 1.0);

  return output;
}
`;

export const PARTICLE_FRAGMENT_SHADER = /* wgsl */`
struct FragmentInput {
  @location(0) color: vec4<f32>,
  @location(1) pointCoord: vec2<f32>,
  @location(2) viewPos: vec3<f32>,
  @location(3) particleRadius: f32,
}

struct RenderParams {
  viewMatrix: mat4x4<f32>,
  projMatrix: mat4x4<f32>,
  invProjMatrix: mat4x4<f32>,
  cameraPos: vec4<f32>,
  screenSize: vec2<f32>,
  particleScale: f32,
  colorMode: u32,
  minVal: f32,
  maxVal: f32,
  _pad0: f32,
  _pad1: f32,
}

@group(0) @binding(0) var<uniform> renderParams: RenderParams;

@fragment
fn main(input: FragmentInput) -> @location(0) vec4<f32> {
  // Convert point coord to [-1, 1] range
  let uv = input.pointCoord * 2.0 - 1.0;
  let dist2 = dot(uv, uv);

  // Discard fragments outside unit circle (sphere impostor)
  if (dist2 > 1.0) {
    discard;
  }

  // Reconstruct sphere normal from point coordinate
  let z = sqrt(1.0 - dist2);
  let normal = normalize(vec3<f32>(uv.x, uv.y, z));

  // Simple Phong lighting
  let lightDir = normalize(vec3<f32>(0.5, 0.8, 1.0)); // Fixed light direction
  let viewDir = normalize(-input.viewPos);
  let halfDir = normalize(lightDir + viewDir);

  // Ambient
  let ambient = 0.3;

  // Diffuse
  let diffuse = max(dot(normal, lightDir), 0.0) * 0.6;

  // Specular (Blinn-Phong)
  let specular = pow(max(dot(normal, halfDir), 0.0), 32.0) * 0.5;

  // Combine lighting
  let lighting = ambient + diffuse + specular;
  let finalColor = input.color.rgb * lighting;

  // Edge darkening for better depth perception
  let edgeFactor = 1.0 - dist2;
  let edgeDarkening = mix(0.8, 1.0, edgeFactor);

  return vec4<f32>(finalColor * edgeDarkening, input.color.a);
}
`;
